import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import { formatDateTime } from "../../lib/format";

function Preview({ d }) {
    return (
        <div className="rounded-md border border-indigo-100 bg-indigo-50/50 p-3" data-testid="launch-notice-preview">
            <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Bell notice preview · opens {d.link}</div>
            <div className="text-sm font-semibold text-slate-900">{d.title}</div>
            <div className="text-xs text-slate-700 mt-0.5">{d.body}</div>
        </div>
    );
}

export function LaunchNoticeDialog({ open, onClose }) {
    const [d, setD] = useState(null);
    const [agree, setAgree] = useState(false);
    const [busy, setBusy] = useState(false);
    const load = () => api.get("/attendance/launch-notice").then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e, "Load failed")));
    useEffect(() => { if (open) { setAgree(false); load(); } }, [open]);
    if (!open) return null;
    const send = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/attendance/launch-notice/send", { confirm: "SEND" });
            toast.success(`Launch notice sent to ${data.notified} employee(s).`);
        } catch (e) {
            toast.error(formatApiError(e, "Send failed"));
        }
        setBusy(false);
        load();
    };
    const rec = d?.record;
    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-xl" data-testid="launch-notice-dialog">
                <DialogHeader>
                    <DialogTitle>Attendance Regularisation launch notice</DialogTitle>
                    <DialogDescription>One-time notice in every active employee's notification bell. No email is sent.</DialogDescription>
                </DialogHeader>
                {d && (
                    <div className="space-y-3 text-sm">
                        <Preview d={d} />
                        {d.sent ? (
                            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-emerald-900" data-testid="launch-notice-sent">
                                Sent on <b>{formatDateTime(rec.sent_at)}</b> by <b>{rec.sent_by}</b> to <b>{rec.notified}</b> employee(s). It cannot be sent again.
                            </div>
                        ) : (
                            <>
                                <div className="rounded-md border border-slate-200 bg-white p-3" data-testid="launch-notice-audience">
                                    Will be delivered to <b data-testid="launch-notice-count">{d.recipients}</b> active employee(s) with a login.
                                    {d.without_login.count > 0 && (
                                        <div className="mt-1 text-xs text-amber-800" data-testid="launch-notice-no-login">
                                            {d.without_login.count} active employee(s) have no login and will not receive it, e.g. {d.without_login.sample.join(", ")}{d.without_login.count > d.without_login.sample.length ? "…" : ""}
                                        </div>
                                    )}
                                </div>
                                {rec?.status === "sending" && <div className="text-xs text-amber-800" data-testid="launch-notice-in-progress">A send started at {formatDateTime(rec.started_at)} is in progress.</div>}
                                <label className="flex items-start gap-2 text-xs text-slate-700">
                                    <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} data-testid="launch-notice-confirm" />
                                    I understand this sends the notice once to {d.recipients} employee(s) and cannot be undone or repeated.
                                </label>
                            </>
                        )}
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="launch-notice-close">Close</Button>
                    {d && !d.sent && (
                        <Button disabled={!agree || busy || rec?.status === "sending"} onClick={send} data-testid="launch-notice-send">
                            {busy ? "Sending…" : `Send to ${d.recipients} employee(s)`}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
